import { Suspense, lazy, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import Nav from './components/Nav.jsx'
import Footer from './components/Footer.jsx'
import 'react-toastify/dist/ReactToastify.css'
import ScrollToTop from "./components/ScrollToTop";
import './App.css'
import PrivacyPolicy from './components/PrivacyPolicy.jsx'
import TermsOfService from './components/TermsOfService.jsx'
import Blog from './pages/blog.jsx'
import Recruitment from './components/Recruitment.jsx'

// Lazy load page components
const Home = lazy(() => import('./pages/Home.jsx'))
const About = lazy(() => import('./pages/About.jsx'))
const Vision = lazy(() => import('./pages/Vision.jsx'))
const Contact = lazy(() => import('./pages/Contact.jsx'))
const Portfolio = lazy(() => import('./pages/Portfolio.jsx'))
const WebDevelopment = lazy(() => import('./servicepage/Webdevelopment.jsx'))
const ApiDevelopment = lazy(() => import('./servicepage/Appdevelopment.jsx'))
const Itsupport = lazy(() => import('./servicepage/Itsupport.jsx'))
const Cloudservices = lazy(() => import('./servicepage/Cloudservices.jsx'))
const DigitalMarketing = lazy(() => import('./servicepage/Digitalmarketing.jsx'))
const Careers = lazy(() => import('./pages/Careers.jsx'))

// Custom loader component
const LoadingFallback = () => {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <svg className="pl" width="240" height="240" viewBox="0 0 240 240">
        <circle className="pl__ring pl__ring--a" cx="120" cy="120" r="105" fill="none" stroke="#000" strokeWidth="20" strokeDasharray="0 660" strokeDashoffset="-330" strokeLinecap="round"></circle>
        <circle className="pl__ring pl__ring--b" cx="120" cy="120" r="35" fill="none" stroke="#000" strokeWidth="20" strokeDasharray="0 220" strokeDashoffset="-110" strokeLinecap="round"></circle>
        <circle className="pl__ring pl__ring--c" cx="85" cy="120" r="70" fill="none" stroke="#000" strokeWidth="20" strokeDasharray="0 440" strokeLinecap="round"></circle>
        <circle className="pl__ring pl__ring--d" cx="155" cy="120" r="70" fill="none" stroke="#000" strokeWidth="20" strokeDasharray="0 440" strokeLinecap="round"></circle>
      </svg>
    </div>
  );
};

// Enhanced page loader that waits for all assets
const PageLoader = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    setIsLoading(true);

    const checkPageLoaded = () => {
      // Check if all images are loaded
      const images = document.images;
      let imagesLoaded = 0;
      const totalImages = images.length;

      if (totalImages === 0) {
        setIsLoading(false);
        return;
      }

      const imageLoadHandler = () => {
        imagesLoaded++;
        if (imagesLoaded === totalImages) {
          setIsLoading(false);
        }
      };

      // Check each image
      for (let i = 0; i < totalImages; i++) {
        if (images[i].complete) {
          imagesLoaded++;
        } else {
          images[i].addEventListener('load', imageLoadHandler);
          images[i].addEventListener('error', imageLoadHandler); // Handle broken images
        }
      }

      if (imagesLoaded === totalImages) {
        setIsLoading(false);
      }
    };

    // Wait for DOM to be ready
    if (document.readyState === 'complete') {
      checkPageLoaded();
    } else {
      window.addEventListener('load', checkPageLoaded);
      return () => window.removeEventListener('load', checkPageLoaded);
    }

    // Fallback timeout in case something goes wrong
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 5000); // Maximum 5 seconds wait

    return () => {
      clearTimeout(timeoutId);
      // Clean up image event listeners
      const images = document.images;
      for (let i = 0; i < images.length; i++) {
        images[i].removeEventListener('load', checkPageLoaded);
        images[i].removeEventListener('error', checkPageLoaded);
      }
    };
  }, [location.pathname]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  return children;
};

// Wrapper component for each route
const RouteWrapper = ({ component: Component }) => {
  return (
    <PageLoader>
      <Component />
    </PageLoader>
  );
};

// Main App Layout component
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isRecruitmentPage = location.pathname === '/recruitment';

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 text-gray-900">
      {!isRecruitmentPage && <Nav />}
      <main className={`flex-1 ${isRecruitmentPage ? '' : 'pt-14 lg:pt-0'}`}>
        {children}
      </main>
      {!isRecruitmentPage && <Footer />}
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop>
        <AppLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<RouteWrapper component={Home} />} />
              <Route path="/about" element={<RouteWrapper component={About} />} />
              <Route path="/vision" element={<RouteWrapper component={Vision} />} />
              <Route path="/contact" element={<RouteWrapper component={Contact} />} />
              <Route path="/blog" element={<RouteWrapper component={Blog} />} />
              <Route path="/services/web-development" element={<RouteWrapper component={WebDevelopment} />} />
              <Route path="/services/app-development" element={<RouteWrapper component={ApiDevelopment} />} />
              <Route path="/services/digital-marketing" element={<RouteWrapper component={DigitalMarketing} />} />
              <Route path="/services/it-support" element={<RouteWrapper component={Itsupport} />} />
              <Route path="/services/cloud" element={<RouteWrapper component={Cloudservices} />} />
              <Route path="/portfolio" element={<RouteWrapper component={Portfolio} />} />
              <Route path="/careers" element={<RouteWrapper component={Careers} />} />
              <Route path="/privacy-policy" element={<RouteWrapper component={PrivacyPolicy} />} />
              <Route path="/terms-of-service" element={<RouteWrapper component={TermsOfService} />} />
              <Route path="/recruitment" element={<RouteWrapper component={Recruitment} />} />
            </Routes>
          </Suspense>
        </AppLayout>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar theme="light" />
      </ScrollToTop>
    </BrowserRouter>
  );
}